import { useState } from 'react';
import { TimeHorizon, ALMData } from '../index';

interface UseTimeHorizonManagerProps {
  initialTimeHorizons: TimeHorizon[];
  initialDayHorizons: TimeHorizon[];
  initialGranularity: "days" | "months";
  almData: ALMData;
  setAlmData: React.Dispatch<React.SetStateAction<ALMData>>;
}

export const useTimeHorizonManager = ({
  initialTimeHorizons,
  initialDayHorizons,
  initialGranularity,
  almData,
  setAlmData
}: UseTimeHorizonManagerProps) => {
  const [granularity, setGranularity] = useState<"days" | "months">(initialGranularity);
  const [timeHorizons, setTimeHorizons] = useState(initialTimeHorizons);
  const [dayHorizons, setDayHorizons] = useState(initialDayHorizons);
  
  // Get the current horizons based on granularity
  const currentHorizons = granularity === "days" ? dayHorizons : timeHorizons;

  const handleGranularityChange = (value: "days" | "months") => {
    setGranularity(value);
  };

  // Add a new time horizon based on current granularity
  const handleAddTimeHorizon = () => {
    if (granularity === "days") {
      // Add a new day horizon
      const highestDay = dayHorizons.reduce((highest, current) => {
        const match = current.label.match(/^(\d+)/);
        if (match) {
          const day = parseInt(match[1], 10);
          return day > highest ? day : highest;
        }
        return highest;
      }, 0);
      
      const nextDay = highestDay + 1;
      const newId = `d${nextDay}`;
      const newLabel = `${nextDay} Day${nextDay !== 1 ? 's' : ''}`;
      
      const updatedAlmData = {...almData};
      const previousHorizon = dayHorizons[dayHorizons.length - 1]?.id;
      
      // Copy the values from the last horizon with slight adjustments
      updatedAlmData.assets = updatedAlmData.assets.map(asset => ({
        ...asset,
        values: {
          ...asset.values,
          [newId]: previousHorizon ? asset.values[previousHorizon] * 0.995 : 100000000
        }
      }));
      
      updatedAlmData.liabilities = updatedAlmData.liabilities.map(liability => ({
        ...liability,
        values: {
          ...liability.values,
          [newId]: previousHorizon ? liability.values[previousHorizon] * 0.998 : 80000000
        }
      }));
      
      updatedAlmData.equity = updatedAlmData.equity.map(equity => ({
        ...equity,
        values: {
          ...equity.values,
          [newId]: previousHorizon ? equity.values[previousHorizon] * 1.003 : 50000000
        }
      }));
      
      setAlmData(updatedAlmData);
      setDayHorizons([...dayHorizons, { id: newId, label: newLabel }]);
    } else {
      // Add a new month horizon
      const highestMonth = timeHorizons.reduce((highest, current) => {
        const match = current.label.match(/^(\d+)/);
        if (match) {
          const month = parseInt(match[1], 10);
          return month > highest ? month : highest;
        }
        return highest;
      }, 0);
      
      const nextMonth = highestMonth + 1;
      const newId = `h${nextMonth}`;
      const newLabel = `${nextMonth} Month${nextMonth !== 1 ? 's' : ''}`;
      
      const updatedAlmData = {...almData};
      const previousHorizon = timeHorizons[timeHorizons.length - 1]?.id;
      
      updatedAlmData.assets = updatedAlmData.assets.map(asset => ({
        ...asset,
        values: {
          ...asset.values,
          [newId]: previousHorizon ? asset.values[previousHorizon] * 0.95 : 100000000
        }
      }));
      
      updatedAlmData.liabilities = updatedAlmData.liabilities.map(liability => ({
        ...liability,
        values: {
          ...liability.values,
          [newId]: previousHorizon ? liability.values[previousHorizon] * 0.97 : 80000000
        }
      }));
      
      updatedAlmData.equity = updatedAlmData.equity.map(equity => ({
        ...equity,
        values: {
          ...equity.values,
          [newId]: previousHorizon ? equity.values[previousHorizon] * 1.01 : 50000000
        }
      }));
      
      setAlmData(updatedAlmData);
      setTimeHorizons([...timeHorizons, { id: newId, label: newLabel }]);
    }
  };

  // Remove a time horizon based on current granularity
  const handleRemoveTimeHorizon = (id: string) => {
    // Don't remove if it's the last one
    if (currentHorizons.length <= 1) return;
    
    if (granularity === "days") {
      setDayHorizons(dayHorizons.filter(horizon => horizon.id !== id));
    } else {
      setTimeHorizons(timeHorizons.filter(horizon => horizon.id !== id));
    }
    
    // Remove data for this horizon
    const updatedAlmData = {...almData};
    
    updatedAlmData.assets = updatedAlmData.assets.map(asset => {
      const newValues = {...asset.values};
      delete newValues[id];
      return {
        ...asset,
        values: newValues
      };
    });
    
    updatedAlmData.liabilities = updatedAlmData.liabilities.map(liability => {
      const newValues = {...liability.values};
      delete newValues[id];
      return {
        ...liability,
        values: newValues
      };
    });
    
    updatedAlmData.equity = updatedAlmData.equity.map(equity => {
      const newValues = {...equity.values};
      delete newValues[id];
      return {
        ...equity,
        values: newValues
      };
    });
    
    setAlmData(updatedAlmData);
  };
  
  return {
    granularity,
    currentHorizons, 
    timeHorizons,
    dayHorizons,
    handleGranularityChange,
    handleAddTimeHorizon,
    handleRemoveTimeHorizon
  };
};

export default useTimeHorizonManager;
